import { Button } from '@/components/ui/button';
import { DatePickerWithRange } from '@/components/ui/date-picker/date-range-picker';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  StatusServerlessTaskLabel,
  TypeServerlessLabel,
} from '@/service/serverless-gpu/type';
import { zodResolver } from '@hookform/resolvers/zod';
import { SearchIcon } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchemaFilter = z.object({
  taskId: z.string().optional(),
  status: z.array(z.string()).optional(),
  type: z.string().optional(),
  date: z
    .object({
      from: z.date().optional(),
      to: z.date().optional(),
    })
    .optional(),
});

export type FormSchemaFilter = z.infer<typeof formSchemaFilter>;

type Props = {
  onSearch: (data: FormSchemaFilter) => void;
};
const ServerlessTaskSearch = ({ onSearch }: Props) => {
  const form = useForm<FormSchemaFilter>({
    resolver: zodResolver(formSchemaFilter),
    defaultValues: {
      taskId: '',
      status: [],
      type: '',
    },
  });

  const optionsStatus = Object.entries(StatusServerlessTaskLabel).map(
    ([value, label]) => ({
      value,
      label,
    })
  );

  const optionsType = Object.entries(TypeServerlessLabel).map(
    ([value, label]) => ({
      value,
      label,
    })
  );

  const onSubmit = async (data: FormSchemaFilter) => {
    onSearch(data);
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-2 lg:flex-row">
            <div className="flex-1">
              <FormField
                control={form.control}
                name="taskId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Search by Task ID"
                        startIcon={
                          <SearchIcon className="h-4 w-4 text-gray-400" />
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <MultiSelect
                        options={optionsStatus}
                        value={field.value || []}
                        onValueChange={field.onChange}
                        placeholder="Select Status"
                        maxCount={1}
                        compact
                        className="w-[220px]"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="w-[120px]">
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {optionsType.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                          <SelectSeparator />
                          <Button
                            className="w-full px-2"
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              field.onChange();
                            }}
                          >
                            Clear
                          </Button>
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <DatePickerWithRange
                        className="w-[250px]"
                        value={field.value as DateRange}
                        onChange={field.onChange}
                        placeholder="Filter Date"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <Button variant="secondary" type="submit">
                Filter
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  );
};

export default ServerlessTaskSearch;
